import { useEffect, useRef } from 'react'
import { useSetRecoilState } from 'recoil'
import { userState } from 'Store/user'
import { useQuickTracking } from '@/provider/QuickTrackingProvider'
import { getRedirectSignInMethod } from '@/lib/auth/redirectState'
import useI18n from 'Hooks/useI18n'

/**
 * popup 被拦时 signInWithRedirect 兜底的回跳入口（见 lib/auth/auth.ts 的 signInWithProvider）。
 *
 * 仅在「popup 被浏览器拦截 → 降级整页跳转 → 授权回跳」这条兜底链路上生效：
 * 此时登录弹窗已卸载，需要在全局常驻处接住 getRedirectResult，跑完 loginFirebase→getUser→setUser。
 * 绝大多数登录走 popup，不经过这里；URL 无回跳标记时不会加载 Firebase 回跳 chunk。
 *
 * 埋点（login_debug 事件，与 popup_blocked_fallback 配对算兜底净救回率）。注意：整页跳转回跳后
 * 内存里的 funnel_sid 已重置，这几个 step 的 funnel_sid 多为空，靠 step 名 + method/status 排查：
 * - redirect_fallback_resolved：回跳成功接住 Firebase 用户
 * - redirect_fallback_login_done：兜底链路 getUser 完成（兜底最终登录结果）
 * - redirect_fallback_failed：兜底链路失败
 */
export const useRedirectSignInFallback = () => {
  const { lang } = useI18n()
  const setUser = useSetRecoilState(userState)
  const { setUserId: setQTUserId } = useQuickTracking()
  // 回跳结果一次性消费，防 StrictMode 双跑重入；不能用 effect cleanup 取消（await 期间会被误标取消）。
  const handledRef = useRef(false)

  useEffect(() => {
    if (handledRef.current || !getRedirectSignInMethod(location.href)) return
    handledRef.current = true
    void import('@/lib/auth/redirectSignInFallback')
      .then(({ completeRedirectSignInFallback }) =>
        completeRedirectSignInFallback({ lang, setUser, setQTUserId })
      )
      .catch((error) => {
        handledRef.current = false
        console.error('Failed to load redirect sign-in fallback:', error)
      })
    // 仅挂载时执行一次。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
